import { Grid, Icon, LoadingSpinner } from "pepsico-ds";
import { Alert, Snackbar } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { dummyData } from "../../config/dummyData";
import "./EngagementHistory.css";

import { useDispatch, useSelector } from "react-redux";
import {
  setIntakeProjectDetails,
  setProjectDetails,
  setProjectError,
  setProjectLoading,
} from "../../redux/actions/projectActions";
import {
  fetchDataByProjectId,
  fetchFormData,
} from "../../services/engagementHistoryService";

import AppTable from "../../shared/appTable";
import { formatSubmittedDate, formatProjectDate } from "../../utils/dateFormat";
import { documentclassification } from "../../utils/fieldConstants";
import ViewCloseOutRequest from "./ViewCloseOutRequest/ViewCloseOutRequest";
import ViewIntakeRequest from "./ViewIntakeRequest/ViewIntakeRequest";
import { CloseOutActions } from "../../redux/closeoutslice/closeoutSlice";
import {
  getRITMActivityData,
  getSnowActivityData,
  getSnowFormTableData,
  setSnowPage,
} from "../../redux/engagementHistory/engagementHistorySlice";
import store from "../../redux/store";
import ViewSnowActivity from "./ViewSnowActivity/ViewSnowActivity";
import { useCanViewCloseOutUnderEngagement } from "../../hooks/access/canViewCloseOutUnderEngagement";
import { CloseOutRadioActions } from "../../redux/closeoutslice/closeOutRadioSlice";

function EngagementHistory() {
  const canViewCloseOutUnderEngagement = useCanViewCloseOutUnderEngagement();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<typeof store.dispatch>();
  const message = location.state?.message || "Default Text";
  const rawData = location.state?.data || [];

  const [historicalData, setHistoricalData] = useState<any[]>([]);

  const [tableData, setTableData] = useState<any[]>([]);

  const [openHistoricalDialog, setOpenHistoricalDialog] = useState(false);
  const [openSnowDialog, setOpenSnowDialog] = useState(false);
  const [historicalFormData, setHistoricalFormData] = React.useState({});

  const [openCloseoutDialog, setOpenCloseoutDialog] = useState(false);
  const [closeoutFormData, setCloseoutFormData] = React.useState({});
  const [isSaveDisabled, setIsSaveDisabled] = useState<any>(true);

  const [isLoading, setIsLoading] = useState(false);

  const [pageLoading, setPageLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { gpid } = useSelector((state: any) => state.auth);
  const {
    isSnowForm,
    isCkms,
    data,
    total,
    page: snowPage,
    pending,
  } = useSelector((state: any) => state.engagementData);
  const {
    isFilesAdded,
    files,
    fileClassificationSelection,
    uploadedFilesSuccess,
  } = useSelector((state: any) => state.closeOut);

  // Fetch data on component mount
  useEffect(() => {
    getData(page, 10);
  }, []);
  useEffect(() => {
    if (canViewCloseOutUnderEngagement) {
      getSnowData(snowPage, 10);
    }
  }, [canViewCloseOutUnderEngagement]);
  const getSnowData = async (page: number, limit: number) => {
    try {
      await dispatch(getSnowFormTableData({ page, limit }));
    } catch (error) {
      console.error("Failed to fetch form data:", error);
    }
  };
  const getData = async (page: number, limit: number) => {
    setPageLoading(true);
    dispatch(CloseOutActions.resetAll());
    try {
      const response = await fetchFormData(gpid, page, limit); // Call the API
      if (response.code === 200) {
        setTableData(response.data); // Set the data array from the response
        setTotalCount(response.total);
        setPage(response.page);
      } else {
        console.error("Error fetching data:", response.message);
      }
    } catch (error) {
      console.error("Failed to fetch form data:", error);
    } finally {
      setPageLoading(false);
    }
  };

  const transformedData = tableData.map((item) => ({
    project_id: item.project_id,
    ritm: item.RITM_number || "N/A",
    projectName: item.project_name || "N/A", // Replace with actual project name if available
    projectStartDate: item.estimated_project_start_date || "N/A",
    projectEndDate: item.estimated_project_end_date || "N/A",
    consultingCompany: item.preferred_vendor || "N/A",
    updatedDate: item.updated_date || "N/A",
    supportingDocuments: item.files.length > 0 ? item.files : "No files",
    edit: "A",
  }));
  const transformedSnowData = data.map((item: any) => ({
    snow_id: item.snow_id,
    Number: item.Number,
    load_timestamp: item.load_timestamp,
    request: item.request,
    assignment_group: item.assignment_group,
    stage: item.stage,
    state: item.state,
    edit: "A",
    closed: item.closed || "N/A",
    closed_by: item.closed_by || "N/A",
  }));

  useEffect(() => {
    if (rawData.length > 0) {
      const startingIndex = dummyData.length + 1;
      const mappedData = rawData.map((item: any, index: number) => {
        const documents = item.data?.supportingDocuments || [];
        const formattedSupportingDocuments = Array.isArray(documents)
          ? documents
          : [];

        return {
          serialNumber: startingIndex + index,
          ritm: item.data?.inputValue || "N/A",
          project_id: item.data?.formattedProjectId || "N/A",
          capability: item.data?.capability || "N/A",
          bandwidth: item.data?.bandwidth || "N/A",
          benchmarking: item.data?.benchmarking || "N/A",
          projectLead: item.data?.projectLead || "N/A",
          projectLeadEmail: item.data?.projectLeadEmail || "N/A",
          stakeholder: item.data?.stakeHolderRequestingGroup || "N/A", // Corrected field name
          financeLead: item.data?.financeLead || "N/A",
          costCenter: item.data?.cost_center || "N/A", // Corrected field name
          sector: item.data?.sector || "N/A",
          geographicScope: item.data?.geographic_scope || "N/A", // Corrected field name
          newOrExtension: item.data?.contract_extension || "N/A", // Corrected field name
          projectName: item.data?.project_name || "N/A", // Corrected field name
          projectBrief: item.data?.Project_brief || "N/A", // Corrected field name
          consultingType: item.data?.consulting_type || "N/A", // Corrected field name
          projectStartDate: formatDate(item.data?.start_date) || "N/A", // Corrected field name
          projectEndDate: formatDate(item.data?.end_data) || "N/A", // Ensure this matches your data
          preferredVendors: item.data?.Preferred_vendor || "N/A", // Corrected field name
          deliverables: item.data?.core_deliverables || "N/A", // Corrected field name
          specialRequirements: item.data?.specialRequirements || "N/A", // Update if needed
          requestedBy: item.data?.requestedBy || "N/A",
          location: item.data?.location || "N/A",
          manager: item.data?.manager || "N/A",
          email: item.data?.email || "N/A",
          financeEmail: item.data?.financeEmail || "N/A",
          consultingCompany: item.data?.consultingCompany || "N/A", // Update if needed
          valueReceivedRating: item.data?.valueReceivedRating || "N/A", // Update if needed
          finalCostUSD: item.data?.service_cost || "N/A", // Update if needed
          dataClassification: item.data?.dataClassification || "N/A", // Update if needed
          purchaseOrderNumber: item.data?.purchaseOrderNumber || "N/A", // Update if needed
          opportunityCost: item.data?.opportunityCost || "N/A", // Update if needed
          finalDeliverables: item.data?.finalDeliverables || "N/A", // Update if needed
          followUpWork: item.data?.follow_up || "N/A", // Corrected field name
          supportingDocuments: formattedSupportingDocuments,
          updatedDate: new Date().toLocaleDateString(),
          coreDeliverables: item.data?.coreDeliverables || "N/A",
          finalCost: item.data?.finalCost || "N/A",
          opportunity_cost: item.data?.opportunityCost || "N/A",
          phasesTwo: item.data?.phasesTwo || "N/A",
          rating: item.data?.rating || "N/A",
          serviceCost: item.data?.serviceCost || "N/A",
          use_others: item.data?.use_others || "N/A",
        };
      });

      const combinedData = [...dummyData, ...mappedData];
      combinedData.sort((a, b) => b.serialNumber - a.serialNumber);
      setHistoricalData(combinedData);
      // setHistoricalData(mappedData.reverse());
    }
  }, [rawData]);

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "N/A") return "N/A"; // Handle invalid or N/A date

    // Assuming the format is "DD-MM-YYYY"
    const parts = dateString.split("-");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month}-${day}`; // Format to "YYYY-MM-DD"
    }

    return "N/A"; // Return 'N/A' for invalid formats
  };

  const handleEdit = async (row: any) => {
    setIsLoading(true);
    // Extract the project_id from the row data
    const projectId = row.project_id;

    try {
      // Call API to fetch data using the projectId
      const response = await fetchDataByProjectId(projectId, gpid);

      // Check if the response code is 200 and if data array contains values
      if (response.data.code === 200) {
        if (response.data.data && response.data.data.length > 0) {
          const projectData = response.data.data[0]; // Assuming only one project data in the array

          // Open the appropriate dialog based on the 'ritm' value
          if (row.ritm === "N/A") {
            dispatch(setIntakeProjectDetails(response.data));
            setHistoricalFormData(projectData);
            setCloseoutFormData(projectData);
            setOpenHistoricalDialog(true);
          } else {
            dispatch(setProjectDetails(response.data));
            setHistoricalFormData(projectData);
            setCloseoutFormData(projectData);
            setOpenCloseoutDialog(true);
          }
          // filesUpdatedRef.current = true;
        } else {
          // No project data found within the array, show a warning
          dispatch(
            CloseOutRadioActions.setSnackBarMessage({
              message: "No data is available for the selected project.",
              severity: "warning",
            })
          );
        }
      } else if (
        response.data.code === 204 &&
        response.data.message === "No Project Data found!"
      ) {
        // Handle the case where no project data is found
        dispatch(setProjectError("No Project Data found!"));
        setHistoricalFormData({});
        setCloseoutFormData({});
        setOpenHistoricalDialog(false);
        setOpenCloseoutDialog(false);
        dispatch(
          CloseOutRadioActions.setSnackBarMessage({
            message: "No data is available for the selected project.",
            severity: "error",
          })
        );
      }
    } catch (error: any) {
      // Dispatch error state to Redux
      dispatch(setProjectError(error.message || "An error occurred"));
    } finally {
      setIsLoading(false);
      dispatch(setProjectLoading(false));
    }
  };

  const handleCloseHistoricalDialog = () => {
    dispatch(setIntakeProjectDetails(null));
    setOpenHistoricalDialog(false);
    dispatch(CloseOutActions.resetAll());
    navigate("/spend-wise-hub/engagement-history", {
      state: { message: "Historical Form", data: [] },
    });
  };

  const handleCloseCloseoutDialog = () => {
    dispatch(setProjectDetails(null));
    setOpenCloseoutDialog(false);
    dispatch(CloseOutActions.resetAll());
    navigate("/spend-wise-hub/engagement-history", {
      state: { message: "Closeout Form" },
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const hasClassified = files.every((file: any) => {
        const is_doc_classification =
          file.document_classification ===
            documentclassification.confidential ||
          file.document_classification === documentclassification.restricted ||
          file.document_classification === documentclassification.internal;
        const is_file_classification_selection =
          fileClassificationSelection[file.name] &&
          (fileClassificationSelection[file.name] ===
            documentclassification.confidentialCatergory ||
            fileClassificationSelection[file.name] ===
              documentclassification.restrictedCategory ||
            fileClassificationSelection[file.name] ===
              documentclassification.internalCategory);

        return is_doc_classification || is_file_classification_selection;
      });
      const newlyUploadedFiles = files.filter(
        (file: any) => !uploadedFilesSuccess.includes(file.name)
      );

      setIsSaveDisabled(
        !(isFilesAdded && hasClassified) || newlyUploadedFiles.length === 0
      );
    }, 200);
    return () => clearTimeout(timer); // Clean up timeout on dependency change
  }, [files, isFilesAdded, uploadedFilesSuccess, fileClassificationSelection]);

  const onPageUpdate = (pageNumber: number) => {
    getData(pageNumber, 10);
    setPage(pageNumber);
  };
  const onSnowPageUpdate = (pageNumber: number) => {
    dispatch(setSnowPage(page));
    getSnowData(pageNumber, 10);
  };
  const OnTableReload = () => {
    getData(page, 10);
  };
  const Columns = [
    {
      columnKey: "ritm",
      header: "RITM No",
    },
    {
      columnKey: "projectName",
      header: "Project Name",
    },
    {
      columnKey: "projectStartDate",
      header: "Start Date",
      cellData: (ctx: any) => {
        return formatProjectDate(ctx.row.original.projectStartDate);
      },
    },
    {
      columnKey: "projectEndDate",
      header: "End Date",
      cellData: (ctx: any) => {
        return formatProjectDate(ctx.row.original.projectEndDate);
      },
    },
    {
      columnKey: "consultingCompany",
      header: "Consulting Vendor",
    },
    {
      columnKey: "updatedDate",
      header: "Submitted Date",
      cellData: (ctx: any) => {
        return formatSubmittedDate(ctx.row.original.updatedDate);
      },
    },
    {
      columnKey: "supportingDocuments",
      header: "No. of docs uploaded",
      cellData: (ctx: any) => {
        return ctx.row.original.supportingDocuments.length;
      },
    },
    {
      columnKey: "edit",
      header: "View",
      cellData: (ctx: any) => (
        <Icon
          icon="remove_red_eye"
          onClick={() => handleEdit(ctx.row.original)}
          style={{ cursor: "pointer" }}
        />
      ),
    },
  ];
  const handleSnowEdit = async (row: any) => {
    setOpenSnowDialog(true);
    dispatch(getSnowActivityData({ snow_id: row.snow_id }));
  };
  const SnowColumns = [
    {
      columnKey: "Number",
      header: "RITM Number",
    },
    {
      columnKey: "load_timestamp",
      header: "Created Date",
      cellData: (ctx: any) => {
        return formatProjectDate(ctx.row.original.load_timestamp);
      },
    },
    {
      columnKey: "request",
      header: "Request",
    },
    {
      columnKey: "stage",
      header: "Stage",
    },
    {
      columnKey: "state",
      header: "State",
    },
    {
      columnKey: "closed",
      header: "Closed",
      cellData: (ctx: any) => {
        return formatProjectDate(ctx.row.original.closed);
      },
    },
    {
      columnKey: "closed_by",
      header: "Closed By",
    },
    {
      columnKey: "edit",
      header: "View",
      cellData: (ctx: any) => (
        <Icon
          icon="remove_red_eye"
          onClick={() => handleSnowEdit(ctx.row.original)}
          style={{ cursor: "pointer" }}
        />
      ),
    },
  ];
  return (
    <div>
      {isCkms && !isSnowForm && (
        <>
          {pageLoading ? (
            <Grid
              style={{
                placeContent: "center",
                minHeight: 200,
                gridTemplateColumns: "auto",
              }}
            >
              <LoadingSpinner
                size={{
                  height: "60px",
                  width: "60px",
                }}
              />
            </Grid>
          ) : (
            <AppTable
              data={transformedData}
              columns={Columns}
              tableLength={totalCount}
              pageNumber={page}
              onPageChange={onPageUpdate}
            />
          )}
        </>
      )}
      {!isCkms && isSnowForm && (
        <>
          {pending ? (
            <Grid
              style={{
                placeContent: "center",
                minHeight: 200,
                gridTemplateColumns: "auto",
              }}
            >
              <LoadingSpinner
                size={{
                  height: "60px",
                  width: "60px",
                }}
              />
            </Grid>
          ) : (
            <AppTable
              data={transformedSnowData}
              columns={SnowColumns}
              tableLength={total}
              pageNumber={snowPage}
              onPageChange={onSnowPageUpdate}
            />
          )}
        </>
      )}
      <ViewSnowActivity
        open={openSnowDialog}
        onclose={() => setOpenSnowDialog(false)}
      />
      <ViewIntakeRequest
        open={openHistoricalDialog}
        onclose={handleCloseHistoricalDialog}
        enabled={isSaveDisabled}
        intakeData={historicalFormData}
        ontableReaload={OnTableReload}
      />
      <ViewCloseOutRequest
        open={openCloseoutDialog}
        onclose={handleCloseCloseoutDialog}
        enabled={isSaveDisabled}
        closeOutData={closeoutFormData}
        ontableReaload={OnTableReload}
      />
    </div>
  );
}

export default EngagementHistory;
